import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { SectionStrip } from '../ui/SectionStrip.js';
import { NotFoundPage, PermissionDeniedPage } from './pages.js';
import { adminHome, adminSectionsFor, holdsAdminSection, useMe, type AdminSectionId } from './me-context.js';

/**
 * The administration module's section strip (02-patterns §2.4): Users · Groups · Audit log,
 * carrying only the sections this user holds. Pages pass it to PageHead's `strip` slot; the
 * dead ends (PermissionDenied, NotFound) carry it too.
 */
export function AdminSectionStrip() {
  const me = useMe();
  return <SectionStrip label="Administration sections" items={adminSectionsFor(me)} />;
}

/**
 * Selection-layer gate for an admin route: renders the page when /api/me says the user holds the
 * section, otherwise the permission-denied page with the strip of sections they do hold. The
 * server enforces regardless; this only avoids sending a reader to a page that will refuse them.
 */
export function AdminRoute({ section, children }: { section: AdminSectionId; children: ReactNode }) {
  const me = useMe();
  if (!holdsAdminSection(me, section)) {
    // Why no crumb: the page has no ancestry of its own (02-patterns §2.1), and the strip is the
    // route out of a dead end (§2.4) — the same shape as AdminNotFound below.
    return <PermissionDeniedPage what="this administration section" strip={<AdminSectionStrip />} />;
  }
  return <>{children}</>;
}

/** The module's own not-found dead end: an unknown /admin address still carries the strip (02-patterns §2.4). */
export function AdminNotFound() {
  return <NotFoundPage strip={<AdminSectionStrip />} />;
}

/** `/admin` lands on the first section the user holds. */
export function AdminIndex() {
  const me = useMe();
  const home = adminHome(me);
  if (!home) return <PermissionDeniedPage what="administration" strip={<AdminSectionStrip />} />;
  return <Navigate to={home} replace />;
}
